import React, { useState } from 'react';
import { EditPageTextField, EditPageButton, EditPageFooter } from '../../common/EditPage';
import { update } from '../../common/v4/BackendProvider';
import { toast } from 'react-toastify';

const PasswordForm = ({ onUpdated }) => {
    const [formData, setFormData] = useState({
        current_password: '',
        new_password: '',
        new_password_confirmation: ''
    });
    const [loading, setLoading] = useState(false);
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const togglePasswordVisibility = (field) => {
        setShowPasswords(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const validateForm = () => {
        if (!formData.current_password.trim()) {
            toast.error('Current password is required');
            return false;
        }

        if (!formData.new_password.trim()) {
            toast.error('New password is required');
            return false;
        }

        if (formData.new_password.length < 8) {
            toast.error('New password must be at least 8 characters long');
            return false;
        }

        if (formData.new_password !== formData.new_password_confirmation) {
            toast.error('New passwords do not match');
            return false;
        }

        if (formData.current_password === formData.new_password) {
            toast.error('New password must be different from current password');
            return false;
        }

        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        try {
            setLoading(true);
            
            await update('/client/profile/password', null, { data: formData });
            
            // Don't show success toast here - parent component handles it
            
            // Reset form
            setFormData({
                current_password: '',
                new_password: '',
                new_password_confirmation: ''
            });
            
            if (onUpdated) {
                onUpdated();
            }
        } catch (error) {
            console.error('Error updating password:', error);
            const errorMessage = error.response?.data?.message || 'Error updating password';
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setFormData({
            current_password: '',
            new_password: '',
            new_password_confirmation: ''
        });
        setShowPasswords({
            current: false,
            new: false,
            confirm: false
        });
    };

    const renderPasswordStrength = () => {
        if (!formData.new_password) return null;

        let strength = 'Weak';
        let strengthClass = 'progress-bar-danger';
        let width = '25%';

        if (formData.new_password.length >= 12) {
            strength = 'Strong';
            strengthClass = 'progress-bar-success';
            width = '100%';
        } else if (formData.new_password.length >= 8) {
            strength = 'Medium';
            strengthClass = 'progress-bar-warning';
            width = '50%';
        }

        return (
            <div className="form-group">
                <label className="control-label col-sm-4">Password Strength</label>
                <div className="col-sm-8">
                    <div className="progress" style={{ marginBottom: '5px' }}>
                        <div 
                            className={`progress-bar ${strengthClass}`}
                            style={{ width: width }}
                        ></div>
                    </div>
                    <small className="text-muted">{strength}</small>
                </div>
            </div>
        );
    };

    return (
        <div className="row">
            <div className="col-md-12">
                <div className="panel panel-default">
                    <div className="panel-body" style={{ paddingBottom: '0' }}>
                        <div className="row">
                            <div className="col-md-6">
                                <div className="form-group">
                                    <label className="control-label col-sm-4">Current Password *</label>
                                    <div className="col-sm-8">
                                        <div className="input-group">
                                            <input
                                                type={showPasswords.current ? "text" : "password"}
                                                className="form-control"
                                                value={formData.current_password}
                                                onChange={(e) => handleInputChange('current_password', e.target.value)}
                                                placeholder="Enter your current password"
                                            />
                                            <span className="input-group-btn">
                                                <button
                                                    type="button"
                                                    className="btn btn-default"
                                                    onClick={() => togglePasswordVisibility('current')}
                                                >
                                                    <i className={`icon-${showPasswords.current ? 'eye-off' : 'eye'}`}></i>
                                                </button>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-6">
                                <div className="form-group">
                                    <label className="control-label col-sm-4">New Password *</label>
                                    <div className="col-sm-8">
                                        <div className="input-group">
                                            <input
                                                type={showPasswords.new ? "text" : "password"}
                                                className="form-control"
                                                value={formData.new_password}
                                                onChange={(e) => handleInputChange('new_password', e.target.value)}
                                                placeholder="Enter your new password"
                                            />
                                            <span className="input-group-btn">
                                                <button
                                                    type="button"
                                                    className="btn btn-default"
                                                    onClick={() => togglePasswordVisibility('new')}
                                                >
                                                    <i className={`icon-${showPasswords.new ? 'eye-off' : 'eye'}`}></i>
                                                </button>
                                            </span>
                                        </div>
                                        <small className="text-muted">Password must be at least 8 characters long</small>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-6">
                                <div className="form-group">
                                    <label className="control-label col-sm-4">Confirm New Password *</label>
                                    <div className="col-sm-8">
                                        <div className="input-group">
                                            <input
                                                type={showPasswords.confirm ? "text" : "password"}
                                                className="form-control"
                                                value={formData.new_password_confirmation}
                                                onChange={(e) => handleInputChange('new_password_confirmation', e.target.value)}
                                                placeholder="Confirm your new password"
                                            />
                                            <span className="input-group-btn">
                                                <button
                                                    type="button"
                                                    className="btn btn-default"
                                                    onClick={() => togglePasswordVisibility('confirm')}
                                                >
                                                    <i className={`icon-${showPasswords.confirm ? 'eye-off' : 'eye'}`}></i>
                                                </button>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {renderPasswordStrength()}
                    </div>

                    <EditPageFooter>
                        <EditPageButton
                            value="Reset"
                            handleClick={handleReset}
                            disabled={loading}
                            bsStyle="default"
                        />
                        <EditPageButton
                            value={loading ? 'Updating...' : 'Update Password'}
                            handleClick={handleSubmit}
                            disabled={loading}
                            bsStyle="primary"
                        />
                    </EditPageFooter>
                </div>
            </div>
        </div>
    );
};

export default PasswordForm; 